import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authenticatedInstance, apiBaseUrl } from "../../../requests";
import type { MatrixTemplate } from "../../../types/matrix";
import { getLeadsQueryKey } from "./utils";

export const useDeleteLeadMatrix = (organizationId: string, leadId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      authenticatedInstance.delete(
        `${apiBaseUrl}/v1/organizations/${organizationId}/leads/${leadId}/matrix`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getLeadsQueryKey(organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: ["organizations", organizationId, "leads", leadId, "matrix"],
      });
    },
  });
};

export const useUploadLeadMatrix = (organizationId: string, leadId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return authenticatedInstance.post(
        `${apiBaseUrl}/v1/organizations/${organizationId}/leads/${leadId}/matrix`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getLeadsQueryKey(organizationId),
      });
    },
  });
};

export const useGetLeadMatrix = (organizationId: string, leadId: string) =>
  useQuery<MatrixTemplate>({
    queryKey: ["organizations", organizationId, "leads", leadId, "matrix"],
    queryFn: () =>
      authenticatedInstance
        .get(
          `${apiBaseUrl}/v1/organizations/${organizationId}/leads/${leadId}/matrix`,
        )
        .then((r) => r.data),
    enabled: !!leadId,
  });

export const useGetLeadProfileMatrix = (
  organizationId: string,
  leadId: string,
  profileLinkId: string,
) =>
  useQuery<MatrixTemplate>({
    queryKey: [
      "organizations",
      organizationId,
      "leads",
      leadId,
      "profiles",
      profileLinkId,
      "matrix",
    ],
    queryFn: () =>
      authenticatedInstance
        .get(
          `${apiBaseUrl}/v1/organizations/${organizationId}/leads/${leadId}/profiles/${profileLinkId}/matrix`,
        )
        .then((r) => r.data),
    enabled: !!profileLinkId,
  });
